import React, { Component } from "react";
import { Search, X, Clock, CornerDownLeft } from "react-feather";
import { withRouter } from "react-router-dom";
import "./searchHeader.css";

const RECENT_KEY = "kd-recent-order-searches";
const MAX_RECENT = 5;

const readRecent = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(RECENT_KEY));
    return Array.isArray(saved) ? saved : [];
  } catch (e) {
    return [];
  }
};

const writeRecent = (list) => {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch (e) {
    // Storage can be blocked; recent searches are only a convenience.
  }
};

// Typing in another field shouldn't trigger the "/" shortcut.
const isTypingTarget = (el) =>
  el &&
  (el.tagName === "INPUT" ||
    el.tagName === "TEXTAREA" ||
    el.tagName === "SELECT" ||
    el.isContentEditable);

const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

export class SearchHeader extends Component {
  constructor(props) {
    super(props);
    this.state = {
      searchFor: "",
      focused: false,
      mobileOpen: false,
      activeIndex: -1,
      recent: readRecent(),
    };
    this.inputRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener("keydown", this.handleShortcut);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleShortcut);
  }

  handleShortcut = (e) => {
    const isCmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k";
    const isSlash = e.key === "/" && !isTypingTarget(e.target);
    if (isCmdK || isSlash) {
      e.preventDefault();
      this.focusInput();
    }
  };

  focusInput = () => {
    this.setState({ mobileOpen: true }, () => {
      if (this.inputRef.current) this.inputRef.current.focus();
    });
  };

  suggestions = () => {
    const query = this.state.searchFor.trim().toLowerCase();
    return this.state.recent.filter(
      (id) => !query || id.toLowerCase().includes(query)
    );
  };

  handleChange = (e) => {
    this.setState({ searchFor: e.target.value, activeIndex: -1 });
  };

  handleKeyDown = (e) => {
    const list = this.suggestions();
    if (e.key === "ArrowDown" && list.length) {
      e.preventDefault();
      this.setState(({ activeIndex }) => ({
        activeIndex: (activeIndex + 1) % list.length,
      }));
    } else if (e.key === "ArrowUp" && list.length) {
      e.preventDefault();
      this.setState(({ activeIndex }) => ({
        activeIndex: activeIndex <= 0 ? list.length - 1 : activeIndex - 1,
      }));
    } else if (e.key === "Escape") {
      if (this.state.searchFor) {
        this.setState({ searchFor: "", activeIndex: -1 });
      } else {
        this.inputRef.current.blur();
      }
    }
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const list = this.suggestions();
    const { activeIndex, searchFor } = this.state;
    this.goTo(activeIndex >= 0 ? list[activeIndex] : searchFor);
  };

  goTo = (orderId) => {
    const id = (orderId || "").trim();
    if (!id) return;
    const recent = [id, ...this.state.recent.filter((r) => r !== id)].slice(
      0,
      MAX_RECENT
    );
    writeRecent(recent);
    this.props.history.push(`/searched-order/${id}`);
    this.setState({
      searchFor: "",
      recent,
      activeIndex: -1,
      focused: false,
      mobileOpen: false,
    });
    if (this.inputRef.current) this.inputRef.current.blur();
  };

  removeRecent = (id) => {
    const recent = this.state.recent.filter((r) => r !== id);
    writeRecent(recent);
    this.setState({ recent, activeIndex: -1 });
  };

  clearRecent = () => {
    writeRecent([]);
    this.setState({ recent: [], activeIndex: -1 });
  };

  clearQuery = () => {
    this.setState({ searchFor: "", activeIndex: -1 });
    this.inputRef.current.focus();
  };

  // Keep focus in the input while clicking inside the dropdown.
  keepFocus = (e) => e.preventDefault();

  render() {
    const { searchFor, focused, mobileOpen, activeIndex, recent } = this.state;
    const list = this.suggestions();
    const hasQuery = searchFor.length > 0;

    return (
      <div
        className={
          "kd-search" +
          (focused ? " is-focused" : "") +
          (mobileOpen ? " is-mobile-open" : "")
        }
      >
        <button
          type="button"
          className="kd-search-mobile-btn"
          aria-label="Search orders"
          onClick={this.focusInput}
        >
          <Search size={18} />
        </button>

        <form className="kd-search-form" role="search" onSubmit={this.handleSubmit}>
          <Search size={16} className="kd-search-icon" aria-hidden="true" />
          <input
            ref={this.inputRef}
            className="kd-search-input"
            name="searchFor"
            type="text"
            autoComplete="off"
            spellCheck={false}
            placeholder="ট্র্যাক অর্ডার..."
            aria-label="Track an order by order ID"
            aria-expanded={focused}
            aria-controls="kd-search-panel"
            aria-activedescendant={
              activeIndex >= 0 ? `kd-search-opt-${activeIndex}` : undefined
            }
            value={searchFor}
            onChange={this.handleChange}
            onKeyDown={this.handleKeyDown}
            onFocus={() => this.setState({ focused: true })}
            onBlur={() =>
              this.setState({ focused: false, mobileOpen: false, activeIndex: -1 })
            }
          />
          {hasQuery ? (
            <button
              type="button"
              className="kd-search-clear"
              aria-label="Clear search"
              onMouseDown={this.keepFocus}
              onClick={this.clearQuery}
            >
              <X size={14} />
            </button>
          ) : (
            <kbd className="kd-search-kbd" aria-hidden="true">
              {isMac ? "⌘K" : "Ctrl K"}
            </kbd>
          )}

          <div
            id="kd-search-panel"
            className="kd-search-panel"
            onMouseDown={this.keepFocus}
          >
            {hasQuery && (
              <button
                type="button"
                className={"kd-search-go" + (activeIndex === -1 ? " is-active" : "")}
                onMouseEnter={() => this.setState({ activeIndex: -1 })}
                onClick={() => this.goTo(searchFor)}
              >
                <Search size={14} />
                <span>
                  Track order <strong>{searchFor.trim()}</strong>
                </span>
                <CornerDownLeft size={14} className="kd-search-enter" />
              </button>
            )}

            {list.length > 0 && (
              <div className="kd-search-section">
                <div className="kd-search-section-head">
                  <span>Recent</span>
                  <button type="button" onClick={this.clearRecent}>
                    Clear all
                  </button>
                </div>
                <ul role="listbox" aria-label="Recent order searches">
                  {list.map((id, index) => (
                    <li
                      key={id}
                      id={`kd-search-opt-${index}`}
                      role="option"
                      aria-selected={index === activeIndex}
                      className={
                        "kd-search-item" + (index === activeIndex ? " is-active" : "")
                      }
                      onMouseEnter={() => this.setState({ activeIndex: index })}
                      onClick={() => this.goTo(id)}
                    >
                      <Clock size={14} />
                      <span className="kd-search-item-id">{id}</span>
                      <button
                        type="button"
                        className="kd-search-remove"
                        aria-label={`Remove ${id} from recent searches`}
                        onClick={(e) => {
                          e.stopPropagation();
                          this.removeRecent(id);
                        }}
                      >
                        <X size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!hasQuery && recent.length === 0 && (
              <p className="kd-search-hint">
                Type an order ID and press <kbd>Enter</kbd> to track it.
              </p>
            )}

            {hasQuery && list.length === 0 && recent.length > 0 && (
              <p className="kd-search-hint">No recent searches match.</p>
            )}

            <div className="kd-search-foot" aria-hidden="true">
              <span><kbd>↑</kbd><kbd>↓</kbd> to move</span>
              <span><kbd>Enter</kbd> to open</span>
              <span><kbd>Esc</kbd> to close</span>
            </div>
          </div>
        </form>
      </div>
    );
  }
}

export default withRouter(SearchHeader);
